import { useEffect } from 'react';
import { viewportOverride } from '../lib/viewport.ts';

/**
 * Fait suivre la hauteur de l'application au clavier logiciel — et à lui seul.
 *
 * # Le problème
 *
 * Quand le clavier s'ouvre sur iOS, Safari **ne rétrécit pas** le layout
 * viewport : `100dvh` continue de désigner l'écran entier, clavier compris. Le
 * pied de page — donc le champ de saisie — se retrouve positionné *derrière*
 * le clavier.
 *
 * # La correction, et sa limite
 *
 * `visualViewport` décrit la zone effectivement visible : on y recopie la
 * hauteur de l'application. `offsetTop` couvre le cas où iOS fait défiler la
 * page sous le clavier au lieu de la redimensionner.
 *
 * Mais **uniquement clavier ouvert**. Sur Android, `visualViewport` bouge à
 * chaque repli de la barre d'adresse : écrire les variables à chaque événement
 * redimensionnait le conteneur `position: fixed` en pleine inertie de
 * défilement, et laissait des bandes non peintes dans la liste
 * (cf. `src/lib/viewport.ts`). Android rétrécissant déjà son layout viewport à
 * l'ouverture du clavier, il n'a de toute façon rien à corriger.
 *
 * Repli : `100dvh` via le CSS, si `visualViewport` n'existe pas.
 */
export function useViewportHeight(): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const root = document.documentElement;
    let applied: string | null = null;

    const update = () => {
      const override = viewportOverride(window.innerHeight, viewport.height, viewport.offsetTop);
      const next = override ? `${override.height}|${override.offset}` : null;

      // Rien n'a changé : on n'écrit pas. Une écriture de style sur la racine
      // pendant un défilement suffit à invalider le rendu du conteneur fixe.
      if (next === applied) return;
      applied = next;

      if (!override) {
        root.style.removeProperty('--app-height');
        root.style.removeProperty('--app-offset');
        return;
      }

      root.style.setProperty('--app-height', `${override.height}px`);
      root.style.setProperty('--app-offset', `${override.offset}px`);
    };

    update();
    viewport.addEventListener('resize', update);
    // `scroll` n'est écouté que pour suivre `offsetTop` clavier ouvert ; hors
    // clavier, `update` sort immédiatement sans rien écrire.
    viewport.addEventListener('scroll', update);

    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      root.style.removeProperty('--app-height');
      root.style.removeProperty('--app-offset');
    };
  }, []);
}
